import React, { useLayoutEffect, useEffect, useState } from 'react';
import clsx from 'clsx';
import Button, { ButtonType } from 'components/Button';
import FormNavigation from 'components/FormNavigation';
import FormContainer from 'components/LoanForm/FormContainer';
import { FlowComponentType } from 'routes/types';
import RangeSlider from 'components/RangeSlider';
import { AdditionalFundsResult } from 'enums/FlowNextResults';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getAdditionalFunds, updateAdditionalFunds } from 'thunks';
import { getApplicationData } from 'selectors/getApplicationData';
import { useSelector } from 'react-redux';
import { HardOfferData } from 'handlers/applicationData';
import Loader from 'components/Loader';

import {
  MIN_SLIDER_VALUE,
  MIN_SLIDER_DISPLAY_THRESHOLD,
  LARGE_LOAN_RANGE_THRESHOLD,
  SMALL_LOAN_STEP_SIZE,
  LARGE_LOAN_STEP_SIZE,
  getAdditionalFundsTitle,
  getAdditionalFundsSubtitle,
  getSliderInitialValue,
  listIconItems,
  meetsAdditionalFundsThreshold,
} from './additionalFundsUtils';

import styles from './AdditionalFunds.module.scss';

const AdditionalFunds = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const { application, isLoading: isLoadingApplication, additionalFundsData, isAdditionalFundsLoading } = useSelector(
    getApplicationData,
  );
  const { minLoanAmount = 0 } = application ?? {};
  const hardOfferData = application?.hardOffer as HardOfferData;
  const {
    additionalFundsTotal: { amount: additionalFundsAmount },
    payOffTotal: { amount: payOffAmount },
  } = hardOfferData;

  const maxSliderValue = additionalFundsData?.maxAmount ?? 0;
  const minSliderValue = Math.max(minLoanAmount - payOffAmount, MIN_SLIDER_VALUE);

  const [sliderValue, setSliderValue] = useState(additionalFundsAmount ?? 0);

  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const sliderShown = maxSliderValue >= MIN_SLIDER_DISPLAY_THRESHOLD;
  const additionalFundsRequired = minSliderValue !== MIN_SLIDER_VALUE;

  useLayoutEffect(() => {
    if (application?.id && !additionalFundsData) {
      dispatchWithUnwrap(getAdditionalFunds(application.id));
    }
  }, [application?.id]);

  useEffect(() => {
    if (!isAdditionalFundsLoading && additionalFundsData && !meetsAdditionalFundsThreshold(additionalFundsData)) {
      handleNext(AdditionalFundsResult.NoChanges);
    }
  }, [additionalFundsData, isAdditionalFundsLoading]);

  useEffect(() => {
    if (additionalFundsData?.maxAmount) {
      setSliderValue(getSliderInitialValue({ additionalFundsAmount, maxSliderValue, minSliderValue }));
    }
  }, [additionalFundsData?.maxAmount]);

  const submit = async (amount: number) => {
    await dispatchWithUnwrap(
      updateAdditionalFunds({
        applicationId: application!.id,
        additionalFundsAmount: amount,
      }),
    );
  };

  const submitExtraFunds = async () => {
    const submitValue = sliderShown ? sliderValue : maxSliderValue;
    if (submitValue === additionalFundsAmount) {
      return handleNext(AdditionalFundsResult.NoChanges);
    }
    await submit(submitValue);
    handleNext(AdditionalFundsResult.ModifiedFunds);
  };

  const skip = async () => {
    const removeCurrentAdditionalFunds =
      !additionalFundsRequired && !sliderShown && additionalFundsAmount > MIN_SLIDER_VALUE;

    if (removeCurrentAdditionalFunds) {
      await submit(MIN_SLIDER_VALUE);
      handleNext(AdditionalFundsResult.ModifiedFunds);
    } else {
      handleNext(AdditionalFundsResult.NoChanges);
    }
  };

  const step = maxSliderValue <= LARGE_LOAN_RANGE_THRESHOLD ? SMALL_LOAN_STEP_SIZE : LARGE_LOAN_STEP_SIZE;

  const title = getAdditionalFundsTitle(additionalFundsRequired, sliderShown, maxSliderValue);
  const subtitle = getAdditionalFundsSubtitle(additionalFundsRequired, sliderShown, minSliderValue);

  if (isAdditionalFundsLoading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader color="#795af7" />
      </div>
    );
  }

  return (
    <>
      <FormNavigation title="Borrow extra funds" {...navigationInfo} />
      <FormContainer title={title} subtitle={subtitle}>
        {sliderShown && (
          <RangeSlider
            min={minSliderValue}
            max={maxSliderValue}
            disabled={isLoadingApplication}
            value={sliderValue}
            step={step}
            onChange={(value) => setSliderValue(value as number)}
          />
        )}
        <div className={styles.itemsContainer}>
          {listIconItems.map((item, index) => (
            <div className={styles.item} key={index}>
              <div className={clsx(styles.icon, item.iconContainer)}>{item.icon}</div>
              <p className={styles.text}>{item.title}</p>
            </div>
          ))}
        </div>
        <div className={styles.buttonsContainer}>
          <Button onClick={submitExtraFunds} isLoading={isLoadingApplication}>
            Next
          </Button>
          {!isLoadingApplication && !additionalFundsRequired && (
            <Button type={ButtonType.Transparent} onClick={skip}>
              Skip
            </Button>
          )}
        </div>
      </FormContainer>
    </>
  );
};

export default AdditionalFunds;
